import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  DemandDTO,
  DemandInvoiceDTO,
  DemandState,
  CreateDemandPayload,
  ProcessDemandPayload,
  DemandQueryParams,
} from '../../types/demand';
import {
  createDemandAPI,
  fetchDemandsAPI,
  fetchOneDemandAPI,
  deleteDemandAPI,
  processDemandAPI,
  fetchDemandInvoicesAPI,
  fetchAllInvoicesAPI,
  fetchOneInvoiceAPI,
} from './demandAPI';
import { RootState } from '../../app/store';

const initialState: DemandState = {
  items: [],
  currentDemand: null,
  invoices: [],
  currentInvoice: null,
  loading: false,
  error: null,
  currentPage: 1,
  totalPages: 0,
  totalItems: 0,
  itemsPerPage: 10,
  hasNext: false,
  hasPrev: false,
};

export const fetchDemands = createAsyncThunk(
  'demand/fetchDemands',
  async (params: DemandQueryParams, { rejectWithValue }) => {
    try {
      const response = await fetchDemandsAPI(params);
      if (!response.status) {
        return rejectWithValue(response);
      }
      if (!response.data) {
        return rejectWithValue({ status: false, message: 'Failed to fetch demands' });
      }
      return {
        response: {
          data: response.data,
          pagination: response.pagination,
        },
        requestedLimit: params.limit,
      };
    } catch (error) {
      return rejectWithValue({ status: false, message: 'Network error occurred' });
    }
  }
);

export const fetchOneDemand = createAsyncThunk(
  'demand/fetchOneDemand',
  async (uuid: string, { rejectWithValue }) => {
    try {
      const response = await fetchOneDemandAPI(uuid);
      if (!response.status) {
        return rejectWithValue(response);
      }
      return response.data as DemandDTO;
    } catch (error) {
      return rejectWithValue({ status: false, message: 'Network error occurred' });
    }
  }
);

export const createDemand = createAsyncThunk(
  'demand/createDemand',
  async (payload: CreateDemandPayload, { rejectWithValue }) => {
    try {
      const response = await createDemandAPI(payload);
      if (!response.status) {
        const errorMessage = response.message || 'Failed to create demand';
        return rejectWithValue({ message: errorMessage });
      }
      return response.data;
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to create demand';
      return rejectWithValue({ message: errorMessage });
    }
  }
);

export const processDemand = createAsyncThunk(
  'demand/processDemand',
  async (
    { uuid, payload }: { uuid: string; payload: ProcessDemandPayload },
    { rejectWithValue }
  ) => {
    try {
      const response = await processDemandAPI(uuid, payload);
      if (!response.status) {
        const errorMessage = response.message || 'Failed to process demand';
        return rejectWithValue({ message: errorMessage });
      }
      return response.data;
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to process demand';
      return rejectWithValue({ message: errorMessage });
    }
  }
);

export const deleteDemand = createAsyncThunk(
  'demand/deleteDemand',
  async (uuid: string, { rejectWithValue }) => {
    try {
      const response = await deleteDemandAPI(uuid);
      if (!response.status) {
        return rejectWithValue(response);
      }
      return uuid;
    } catch (error) {
      return rejectWithValue({ status: false, message: 'Network error occurred' });
    }
  }
);

export const fetchDemandInvoices = createAsyncThunk(
  'demand/fetchDemandInvoices',
  async (demandUuid: string, { rejectWithValue }) => {
    try {
      const response = await fetchDemandInvoicesAPI(demandUuid);
      if (!response.status) {
        return rejectWithValue(response);
      }
      return response.data as DemandInvoiceDTO[];
    } catch (error) {
      return rejectWithValue({ status: false, message: 'Network error occurred' });
    }
  }
);

export const fetchAllInvoices = createAsyncThunk(
  'demand/fetchAllInvoices',
  async (params: DemandQueryParams, { rejectWithValue }) => {
    try {
      const response = await fetchAllInvoicesAPI(params);
      if (!response.status) {
        return rejectWithValue(response);
      }
      return {
        response: {
          data: response.data,
          pagination: response.pagination,
        },
        requestedLimit: params.limit,
      };
    } catch (error) {
      return rejectWithValue({ status: false, message: 'Network error occurred' });
    }
  }
);

export const fetchOneInvoice = createAsyncThunk(
  'demand/fetchOneInvoice',
  async (invoiceUuid: string, { rejectWithValue }) => {
    try {
      const response = await fetchOneInvoiceAPI(invoiceUuid);
      if (!response.status) {
        return rejectWithValue(response);
      }
      return response.data as DemandInvoiceDTO;
    } catch (error) {
      return rejectWithValue({ status: false, message: 'Network error occurred' });
    }
  }
);

const demandSlice = createSlice({
  name: 'demand',
  initialState,
  reducers: {
    resetDemand: (state) => {
      state.items = [];
      state.currentDemand = null;
      state.invoices = [];
      state.currentInvoice = null;
      state.loading = false;
      state.error = null;
      state.currentPage = 1;
      state.totalPages = 0;
      state.totalItems = 0;
    },
    clearCurrentDemand: (state) => {
      state.currentDemand = null;
    },
    clearInvoices: (state) => {
      state.invoices = [];
      state.currentInvoice = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch demands
      .addCase(fetchDemands.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDemands.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;

        const { response, requestedLimit } = action.payload as any;

        if (response && response.data && Array.isArray(response.data) && response.pagination) {
          state.items = response.data;
          state.currentPage = response.pagination.currentPage;
          state.totalPages = response.pagination.totalPages;
          state.totalItems = response.pagination.totalItems;
          state.itemsPerPage = requestedLimit || response.pagination.pageSize;
          state.hasNext = response.pagination.hasNext;
          state.hasPrev = response.pagination.hasPrev;
        } else if (Array.isArray(response)) {
          state.items = response;
          state.currentPage = 1;
          state.totalPages = 1;
          state.totalItems = response.length;
          state.itemsPerPage = requestedLimit || 10;
          state.hasNext = false;
          state.hasPrev = false;
        } else {
          state.items = [];
        }
      })
      .addCase(fetchDemands.rejected, (state, action) => {
        state.loading = false;
        const errorResponse = action.payload as any;
        state.error = errorResponse?.message || action.error.message || 'Failed to fetch demands';
      })

      // Fetch one demand
      .addCase(fetchOneDemand.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOneDemand.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.currentDemand = action.payload as DemandDTO;
      })
      .addCase(fetchOneDemand.rejected, (state, action) => {
        state.loading = false;
        const errorResponse = action.payload as any;
        state.error = errorResponse?.message || action.error.message || 'Failed to fetch demand';
      })

      // Create demand
      .addCase(createDemand.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createDemand.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(createDemand.rejected, (state, action) => {
        state.loading = false;
        const errorResponse = action.payload as any;
        state.error = errorResponse?.message || action.error.message || 'Failed to create demand';
      })

      // Process demand
      .addCase(processDemand.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(processDemand.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.currentDemand = action.payload as DemandDTO;
      })
      .addCase(processDemand.rejected, (state, action) => {
        state.loading = false;
        const errorResponse = action.payload as any;
        state.error = errorResponse?.message || action.error.message || 'Failed to process demand';
      })

      // Delete demand
      .addCase(deleteDemand.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteDemand.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(deleteDemand.rejected, (state, action) => {
        state.loading = false;
        const errorResponse = action.payload as any;
        state.error = errorResponse?.message || action.error.message || 'Failed to delete demand';
      })

      // Fetch demand invoices
      .addCase(fetchDemandInvoices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDemandInvoices.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.invoices = action.payload as DemandInvoiceDTO[];
      })
      .addCase(fetchDemandInvoices.rejected, (state, action) => {
        state.loading = false;
        const errorResponse = action.payload as any;
        state.error = errorResponse?.message || action.error.message || 'Failed to fetch invoices';
      })

      // Fetch all invoices
      .addCase(fetchAllInvoices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllInvoices.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;

        const { response, requestedLimit } = action.payload as any;

        if (response && response.data && Array.isArray(response.data) && response.pagination) {
          state.invoices = response.data;
          state.currentPage = response.pagination.currentPage;
          state.totalPages = response.pagination.totalPages;
          state.totalItems = response.pagination.totalItems;
          state.itemsPerPage = requestedLimit || response.pagination.pageSize;
          state.hasNext = response.pagination.hasNext;
          state.hasPrev = response.pagination.hasPrev;
        } else {
          state.invoices = [];
        }
      })
      .addCase(fetchAllInvoices.rejected, (state, action) => {
        state.loading = false;
        const errorResponse = action.payload as any;
        state.error = errorResponse?.message || action.error.message || 'Failed to fetch invoices';
      })

      // Fetch one invoice
      .addCase(fetchOneInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOneInvoice.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.currentInvoice = action.payload as DemandInvoiceDTO;
      })
      .addCase(fetchOneInvoice.rejected, (state, action) => {
        state.loading = false;
        const errorResponse = action.payload as any;
        state.error = errorResponse?.message || action.error.message || 'Failed to fetch invoice';
      });
  },
});

export const { resetDemand, clearCurrentDemand, clearInvoices, clearError } = demandSlice.actions;

export const selectDemands = (state: RootState) => state.demand.items;
export const selectCurrentDemand = (state: RootState) => state.demand.currentDemand;
export const selectDemandInvoices = (state: RootState) => state.demand.invoices;
export const selectCurrentInvoice = (state: RootState) => state.demand.currentInvoice;
export const selectDemandLoading = (state: RootState) => state.demand.loading;
export const selectDemandError = (state: RootState) => state.demand.error;
export const selectDemandPagination = (state: RootState) => ({
  currentPage: state.demand.currentPage,
  totalPages: state.demand.totalPages,
  totalItems: state.demand.totalItems,
  itemsPerPage: state.demand.itemsPerPage,
  hasNext: state.demand.hasNext,
  hasPrev: state.demand.hasPrev,
});

export default demandSlice.reducer;
